import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
  useToast,
} from '@shared/ui';
import { createApiClient } from '@shared/api/client/api-client';
import { createAuthApi } from '@shared/api/lib/auth-api';
import { authStorage } from '@shared/api/client/auth-storage';
import { useAuth } from '@entities/auth';
import { map2FASetupDtoToEntity } from '@entities/auth';

import { env } from '@shared/config/env';

interface EnableTwoFactorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EnableTwoFactorModal({ open, onOpenChange }: EnableTwoFactorModalProps) {
  const { t } = useTranslation('auth');
  const { toast } = useToast();
  const { refreshUser } = useAuth();

  const [code, setCode] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isQrLoading, setIsQrLoading] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setCode('');
      setValidationError(null);
      return;
    }

    const authApi = createAuthApi(createApiClient({ baseURL: env.API_BASE_URL }));

    setIsQrLoading(true);
    authApi
      .enable2fa()
      .then((response) => {
        const setup = map2FASetupDtoToEntity(response);
        setQrCode(setup.qrCode);
      })
      .catch((err: unknown) => {
        toast((err as { message?: string })?.message || t('errors.verificationFailed'), 'error');
      })
      .finally(() => {
        setIsQrLoading(false);
      });
  }, [open, t, toast]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!/^\d{6}$/.test(code)) {
      setValidationError(t('errors.codeInvalid'));
      return;
    }

    setIsLoading(true);
    setValidationError(null);

    try {
      const authApi = createAuthApi(createApiClient({ baseURL: env.API_BASE_URL }));
      const response = await authApi.enable2faConfirm({ code });

      if (response.token) {
        authStorage.setAccessToken(response.token);
      }

      await refreshUser();
      toast(t('success.2faEnabled'), 'success');
      onOpenChange(false);
    } catch (err: unknown) {
      toast((err as { message?: string })?.message || t('errors.verificationFailed'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('twoFactorAuth')}</DialogTitle>
          <DialogDescription>{t('scanQrCode')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex justify-center">
            {isQrLoading ? (
              <Skeleton className="h-48 w-48 rounded-lg" />
            ) : qrCode ? (
              <img src={qrCode} alt="2FA QR" className="h-48 w-48 rounded-lg border border-border" />
            ) : null}
          </div>

          <div className="space-y-2">
            <Label htmlFor="enable-2fa-code">{t('enterAuthCode')}</Label>
            <Input
              id="enable-2fa-code"
              value={code}
              maxLength={6}
              onChange={(event) => {
                setCode(event.target.value.replace(/\D/g, ''));
                if (validationError) setValidationError(null);
              }}
              placeholder="000000"
              error={validationError ?? undefined}
            />
          </div>

          <Button type="submit" className="w-full" loading={isLoading} disabled={code.length !== 6}>
            {t('enable2FA')}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
